import {
  X,
  Copy,
  Check,
  Info,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SystemLanguage } from "../../common/system-language";

export interface ShareResponse {
  token: string;
  shareUrl: string;
}

interface ShareModalProps {
  open: boolean;
  loading: boolean;
  data?: ShareResponse | null;
  onClose: () => void;
  systemLanguage: SystemLanguage;
}

export function ShareModal({
  open,
  loading,
  data,
  onClose,
  systemLanguage,
}: ShareModalProps) {
  const isRTL = systemLanguage === "ar";
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fullUrl = data
    ? `${window.location.origin}${data.shareUrl}`
    : "";

  useEffect(() => {
    if (fullUrl && inputRef.current) {
      inputRef.current.select();
    }
  }, [fullUrl]);

  if (!open) return null;

  const t = (key: string) => {
    const translations: Record<string, Record<string, string>> = {
      en: {
        publicLink: "Public Link",
        info: "Anyone with the link can view this shared conversation.",
        generatingLink: "Generating link...",
        copied: "Copied",
        copy: "Copy",
        shareVia: "Or share via",
      },
      ar: {
        publicLink: "رابط عام",
        info: "يمكن لأي شخص لديه الرابط عرض هذه المحادثة المشتركة.",
        generatingLink: "جاري إنشاء الرابط...",
        copied: "تم النسخ",
        copy: "نسخ",
        shareVia: "أو شارك عبر",
      },
    };
    return translations[systemLanguage]?.[key] || translations.en[key] || key;
  };

  const copyLink = async () => {
    if (!fullUrl) return;
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl p-6">
        {/* Close button */}
        <button
          onClick={onClose}
          className={`absolute top-4 ${
            isRTL ? "left-4" : "right-4"
          } text-gray-400 hover:text-gray-600 transition`}
        >
          <X size={18} />
        </button>

        {/* Header */}
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t("publicLink")}
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          {t("info")}
        </p>

        {loading ? (
          <div className="py-12 text-center text-gray-500">
            {t("generatingLink")}
          </div>
        ) : (
          <>
            {/* Link */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("publicLink")}
              </label>

              <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-xl px-3 py-2">
                <input
                  ref={inputRef}
                  readOnly
                  value={fullUrl}
                  dir="ltr"
                  className="flex-1 bg-transparent text-sm font-mono text-gray-800 outline-none truncate"
                />

                <button
                  onClick={copyLink}
                  className="flex items-center gap-1 text-sm font-medium text-[#33f] hover:opacity-80 transition"
                >
                  {copied ? (
                    <>
                      <Check size={16} />
                      {t("copied")}
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      {t("copy")}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info */}
            <div
              className={`flex items-start gap-2 text-xs text-gray-500 mb-6 ${
                isRTL ? "flex-row-reverse text-right" : "text-left"
              }`}
            >
              <Info size={14} className="mt-[1px]" />
              <span>{t("info")}</span>
            </div>

            {/* Share */}
            <div>
              <p className="text-xs font-medium text-gray-600 mb-3 text-center">
                {t("shareVia")}
              </p>

              <div
                className={`flex items-center justify-center gap-6 ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-[#33f] hover:text-white transition"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>

                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-[#33f] hover:text-white transition"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>

                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(fullUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-[#33f] hover:text-white transition"
                >
                  <span className="text-sm font-bold">X</span>
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
